import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

export interface CmpConfigProviderDto {
  ConfigID: number;
  ProviderID: string;
  DeployStatus: 'pending' | 'deployed' | 'error';
  DeployedAt: string | null;
  DeployError: string | null;
  DeployedBy: string | null;
  GcsUrl: string | null;
  CoreVersion: string | null;
}

export interface CmpVendorDto {
  id: number;
  name: string;
  policyUrl: string;
}

export interface CmpConfigDto {
  ID: number;
  Name: string;
  PublisherID: string;
  CmpId: number;
  CmpVersion: number;
  ConsentLanguage: string;
  PublisherCC: string;
  VendorListVersion: number;
  AutoShow: 0 | 1;
  Publisher: 0 | 1;
  SelectedPurposes: number[];
  Vendors: CmpVendorDto[];
  PrivacyPolicyUrl: string | null;
  I18n: Record<string, string> | null;
  CookieName: string;
  CookieExpireDays: number;
  OnConsentSavedScript: string | null;
  Status: 0 | 1;
  CreatedAt: string;
  UpdatedAt: string;
  CoreVersion: string;
  Providers: CmpConfigProviderDto[];
  CurrentCoreVersion: string;
  AvailableCoreVersions: string[];
}

export interface GvlDto {
  vendorListVersion: number;
  lastUpdated: string;
  vendors: CmpVendorDto[];
}

export interface CreateCmpConfigBody {
  Name: string;
  PublisherID: string;
  CmpId?: number;
  CoreVersion?: string;
  ConsentLanguage?: string;
  PublisherCC?: string;
  VendorListVersion?: number;
  AutoShow?: boolean;
  Publisher?: boolean;
  SelectedPurposes?: number[];
  Vendors?: CmpVendorDto[];
  PrivacyPolicyUrl?: string | null;
  I18n?: Record<string, string> | null;
  CookieName?: string;
  CookieExpireDays?: number;
  OnConsentSavedScript?: string | null;
  Status?: number;
  ProviderIDs?: string[];
}

export interface UpdateCmpConfigBody extends Partial<CreateCmpConfigBody> {
  ID: number;
}

export interface CmpDeployResultDto {
  deployed: string[];
  errors: { providerId: string; error: string }[];
}

export interface CmpPurgeResultDto {
  invalidated: string[];
  errors: { path: string; error: string }[];
  skipped: boolean;
}

interface GenericResponse<T> {
  payload?: T;
  error?: { status: number; message: string };
}

@Injectable({ providedIn: 'root' })
export class CmpApiService {
  private readonly fallbackBase = this.normalizeBase(environment.apiUrl);

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  getConfigs(): Observable<CmpConfigDto[]> {
    const ep = this.composeEndpoint('/webapp/cmp/configs');
    return this.http.get<GenericResponse<CmpConfigDto[]>>(ep.url, { headers: this.buildHeaders(ep.useRemote) }).pipe(
      map(res => res.payload ?? []),
      catchError(err => { console.error('[cmp-api] list failed', err); return throwError(() => err); })
    );
  }

  getConfig(id: number): Observable<CmpConfigDto> {
    const ep = this.composeEndpoint(`/webapp/cmp/configs/${id}`);
    return this.http.get<GenericResponse<CmpConfigDto>>(ep.url, { headers: this.buildHeaders(ep.useRemote) }).pipe(
      map(res => res.payload!),
      catchError(err => { console.error('[cmp-api] get failed', err); return throwError(() => err); })
    );
  }

  createConfig(body: CreateCmpConfigBody): Observable<CmpConfigDto> {
    const ep = this.composeEndpoint('/webapp/cmp/configs');
    return this.http.post<GenericResponse<CmpConfigDto>>(ep.url, body, { headers: this.buildHeaders(ep.useRemote) }).pipe(
      map(res => res.payload!),
      catchError(err => { console.error('[cmp-api] create failed', err); return throwError(() => err); })
    );
  }

  updateConfig(body: UpdateCmpConfigBody): Observable<CmpConfigDto> {
    const ep = this.composeEndpoint('/webapp/cmp/configs');
    return this.http.put<GenericResponse<CmpConfigDto>>(ep.url, body, { headers: this.buildHeaders(ep.useRemote) }).pipe(
      map(res => res.payload!),
      catchError(err => { console.error('[cmp-api] update failed', err); return throwError(() => err); })
    );
  }

  deleteConfig(id: number): Observable<{ ID: number }> {
    const ep = this.composeEndpoint('/webapp/cmp/configs');
    return this.http.delete<GenericResponse<{ ID: number }>>(ep.url, {
      headers: this.buildHeaders(ep.useRemote),
      body: { ID: id }
    }).pipe(
      map(res => res.payload ?? { ID: id }),
      catchError(err => { console.error('[cmp-api] delete failed', err); return throwError(() => err); })
    );
  }

  deploy(configId: number, providerIds?: string[]): Observable<CmpDeployResultDto> {
    const ep = this.composeEndpoint('/webapp/cmp/deploy');
    const body: any = { configId };
    if (providerIds && providerIds.length > 0) { body.providerIds = providerIds; }
    return this.http.post<GenericResponse<CmpDeployResultDto>>(ep.url, body, { headers: this.buildHeaders(ep.useRemote) }).pipe(
      map(res => res.payload ?? { deployed: [], errors: [] }),
      catchError(err => { console.error('[cmp-api] deploy failed', err); return throwError(() => err); })
    );
  }

  purge(configId: number, providerIds?: string[]): Observable<CmpPurgeResultDto> {
    const ep = this.composeEndpoint('/webapp/cmp/purge');
    const body: any = { configId };
    if (providerIds && providerIds.length > 0) { body.providerIds = providerIds; }
    return this.http.post<GenericResponse<CmpPurgeResultDto>>(ep.url, body, { headers: this.buildHeaders(ep.useRemote) }).pipe(
      map(res => res.payload ?? { invalidated: [], errors: [], skipped: false }),
      catchError(err => { console.error('[cmp-api] purge failed', err); return throwError(() => err); })
    );
  }

  getGvl(): Observable<GvlDto> {
    const ep = this.composeEndpoint('/webapp/cmp/gvl');
    return this.http.get<GenericResponse<GvlDto>>(ep.url, { headers: this.buildHeaders(ep.useRemote) }).pipe(
      map(res => res.payload!),
      catchError(err => { console.error('[cmp-api] gvl failed', err); return throwError(() => err); })
    );
  }

  private composeEndpoint(path: string): { url: string; useRemote: boolean } {
    const info = this.getBaseUrlInfo();
    if (!path.startsWith('/')) path = `/${path}`;
    return { url: `${info.baseUrl}${path}`, useRemote: info.useRemote };
  }

  private buildHeaders(useRemote: boolean): HttpHeaders {
    if (useRemote) {
      let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      const token = this.authService.getTncApiToken();
      if (token) headers = headers.set('Authorization', token);
      return headers;
    }
    let headers = new HttpHeaders(this.authService.getAuthHeaders());
    headers = headers.set('Content-Type', 'application/json');
    return headers;
  }

  private getBaseUrlInfo(): { baseUrl: string; useRemote: boolean } {
    const remoteBase = this.authService.getTncApiBaseUrl();
    const remoteToken = this.authService.getTncApiToken();
    if (remoteBase && remoteToken) {
      return { baseUrl: this.normalizeBase(remoteBase), useRemote: true };
    }
    return { baseUrl: this.fallbackBase, useRemote: false };
  }

  private normalizeBase(url: string): string {
    if (!url) return '';
    return url.replace(/\/+$/u, '');
  }
}
